/**
 * icarusChat – typed wrapper for the `icarus-chat` Supabase Edge Function.
 *
 * Why a service layer?
 *   ADR-0002 establishes the repository pattern for `supabase.from()` data
 *   access. Edge functions were left out of scope; that turned into ~19
 *   ad-hoc `supabase.functions.invoke('icarus-chat', ...)` call sites
 *   spread across pages and components, each rolling its own error
 *   handling and request shape. This module is the agreed-upon home for
 *   that wiring (see `services/README.md`).
 *
 * Ein Body-Shape für alle Oberflächen
 *   Der Body wird ausschließlich über `buildChatRequestBody`
 *   (`@/lib/ai/chatRequestContract`) gebaut — dieselbe Datei, die die Edge
 *   Function als Deno-Mirror einliest. Vorher hatte jede Oberfläche ihren
 *   eigenen Shape; der des globalen Assistenten passte zu nichts, was die
 *   Function las, und lief in jedem Fall in `400 Nachricht fehlt`.
 *   Callers geben deshalb keine rohen Felder mehr an, sondern die
 *   semantischen Eingaben des Vertrags.
 *
 * Kein Streaming: `supabase.functions.invoke` kann keine SSE-Chunks lesen,
 * und `icarus-chat` streamt auch nicht — es fährt pro Anfrage einen
 * Tool-Loop (bis zu 5 Runden) und antwortet am Stück. Wer eine
 * Tipp-Animation braucht, nutzt den `pending`-State von `useChatStream`.
 */

import { supabase } from '@/integrations/supabase/client';
import { parseFunctionsError } from '@/lib/supabase-helpers';
import {
  buildChatRequestBody,
  type BuildChatBodyInput,
  type ExecutePlanPayload,
  type TurnMessage,
} from '@/lib/ai/chatRequestContract';

export type IcarusChatMessage = TurnMessage;
export type { BuildChatBodyInput as IcarusChatRequest, ExecutePlanPayload };

export interface IcarusChatResponse {
  response?: string;
  message?: string;
  plan?: unknown;
  /** Set by the edge function when it surfaces validation/quota errors via the response body instead of HTTP. */
  error?: string;
  /** `'plan'` when the function returns a tool-call plan (plan-mode). */
  type?: string;
  steps?: unknown[];
  [key: string]: unknown;
}

/**
 * Invoke `icarus-chat` and return a typed response.
 *
 * Errors are normalised through `parseFunctionsError` so callers always
 * receive a plain `Error` with a user-readable message — matching the
 * convention used elsewhere in this service layer.
 */
export async function callIcarusChat(
  input: BuildChatBodyInput,
): Promise<IcarusChatResponse> {
  const { data, error } = await supabase.functions.invoke<IcarusChatResponse>(
    'icarus-chat',
    { body: buildChatRequestBody(input) },
  );

  if (error) {
    const message = await parseFunctionsError(error);
    throw new Error(message);
  }

  if (!data) {
    throw new Error('icarus-chat returned an empty response');
  }

  return data;
}
